import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Monitor, Smartphone, Tablet, Globe, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import SessionReplayPublic from '../components/sessions/SessionReplayPublic';
import { formatDateTime, formatDurationMs } from '../utils/formatters';

const publicApi = axios.create({ baseURL: API_BASE_URL });

async function getSharedSession(token: string) {
  const res = await publicApi.get(`/share/${token}`);
  return res.data.session;
}

function getDeviceIcon(device: string) {
  switch ((device || '').toLowerCase()) {
    case 'mobile': return <Smartphone className="h-4 w-4" />;
    case 'tablet': return <Tablet className="h-4 w-4" />;
    default: return <Monitor className="h-4 w-4" />;
  }
}

export default function SharePage() {
  const { token } = useParams<{ token: string }>();

  const { data: session, isLoading, error } = useQuery({
    queryKey: ['shared-session', token],
    queryFn: () => getSharedSession(token!),
    enabled: !!token,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-gray-400" />
          <p className="mt-3 text-sm text-gray-500">Loading shared session...</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md rounded-xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <AlertTriangle className="mx-auto h-10 w-10 text-red-400" />
          <h2 className="mt-4 text-lg font-semibold text-gray-900">Session Not Found</h2>
          <p className="mt-2 text-sm text-gray-500">
            This share link is invalid or has been revoked by the owner.
          </p>
        </div>
      </div>
    );
  }

  const device = session.deviceType || '';
  const browser = session.browser || '';
  const browserVersion = session.browserVersion || '';
  const os = session.os || '';
  const durationMs = session.durationMs ?? 0;
  const pagesVisited = session.pagesVisited ?? 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white px-6 py-3">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded bg-brand-600" />
            <span className="text-sm font-semibold text-gray-900">Vantage</span>
            <span className="text-sm text-gray-400">· Shared Session Recording</span>
          </div>
          <span className="text-xs text-gray-400">View-only · Shared by owner</span>
        </div>
      </header>

      <div className="mx-auto max-w-7xl space-y-3 p-6">
        {/* Session Metadata Bar */}
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-xs">
          <div className="flex items-center gap-1.5 text-gray-600">
            {getDeviceIcon(device)}
            <span className="font-medium capitalize">{device || 'Desktop'}</span>
            {browser && (
              <span className="text-gray-400">
                {browser} {browserVersion ? browserVersion.split('.')[0] : ''}
              </span>
            )}
            {os && <span className="text-gray-400">/ {os}</span>}
          </div>

          <span className="text-gray-200">|</span>
          <span className="text-gray-500">
            {formatDurationMs(durationMs)} · {pagesVisited} page{pagesVisited !== 1 ? 's' : ''}
          </span>

          <span className="text-gray-200">|</span>
          <span className="text-gray-500">{formatDateTime(session.startedAt)}</span>

          {(session.hasRageClicks || session.hasErrors) && (
            <>
              <span className="text-gray-200">|</span>
              <div className="flex items-center gap-1.5">
                {session.hasRageClicks && (
                  <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">rage clicks</span>
                )}
                {session.hasErrors && (
                  <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">errors</span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Player */}
        <SessionReplayPublic token={token!} autoPlay={true} />
      </div>
    </div>
  );
}
